import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CiamConfig } from 'src/environments/ciam-config';
import { CiamCustomer, CiamTokenResponse, CiamTokens } from './ciam.models';
import { StorageService } from './storage.service';

const RETURN_URL_KEY = 'ciam-return-url';
const DEFAULT_RETURN_URL = '/accueil';

@Injectable({
  providedIn: 'root',
})
export class CiamService {
  private readonly config = environment.ciam as CiamConfig;
  private refreshInFlight$?: Observable<CiamTokens>;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly storageService: StorageService,
  ) {}

  redirectToSignIn(returnUrl = DEFAULT_RETURN_URL): void {
    this.redirectToAuthApp(this.config.signInPath, returnUrl, 'redirectTo');
  }

  redirectToRegister(returnUrl = '/creation'): void {
    this.redirectToAuthApp(this.config.registerPath, returnUrl, 'redirectTo');
  }

  redirectToForgotPassword(returnUrl = DEFAULT_RETURN_URL): void {
    this.redirectToAuthApp(this.config.forgotPasswordPath, returnUrl, 'redirectTo');
  }

  redirectToActivation(resumeUrl: string, returnUrl = DEFAULT_RETURN_URL): void {
    this.redirectToAuthApp(this.config.activationPath, returnUrl, 'resume_url', resumeUrl);
  }

  completeAuthentication(): Observable<CiamCustomer> {
    return this.exchangeTokens().pipe(
      switchMap(() => this.getCurrentCustomer()),
      tap(customer => this.storeCustomerIdentity(customer)),
    );
  }

  exchangeTokens(): Observable<CiamTokens> {
    this.assertEnabled();

    return this.http
      .get<CiamTokenResponse>(this.bffUrl(this.config.bff.exchangePath), {
        withCredentials: true,
      })
      .pipe(
        map(response => this.storageService.saveTokens(response)),
      );
  }

  /**
   * GAuth n'expose pas de rotation par refresh token pour le web : renouveler une session
   * consiste à ré-échanger le cookie AuthApp, qui en est la source de vérité. Le refresh token
   * reçu n'est donc pas utilisé côté navigateur.
   */
  refreshTokens(): Observable<CiamTokens> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .get<CiamTokenResponse>(this.bffUrl(this.config.bff.exchangePath), { withCredentials: true })
      .pipe(
        map(response => this.storageService.saveTokens(response)),
        catchError(error => {
          if (this.isDefinitiveRefreshRejection(error)) {
            this.storageService.clean();
          }

          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshInFlight$;
  }

  ensureAuthenticated(): Observable<boolean> {
    // Aucun jeton local ne signifie plus « déconnecté » : un cookie AuthApp valide suffit à
    // reprendre la session, par exemple dans un nouvel onglet.
    const session$ = this.storageService.hasValidAccessToken()
      ? of(undefined)
      : this.refreshTokens();

    return session$.pipe(
      switchMap(() => this.ensureCustomerIdentity()),
      map(() => true),
      catchError(error => this.isDefinitiveRefreshRejection(error)
        ? of(false)
        : throwError(() => error)),
    );
  }

  getCurrentCustomer(): Observable<CiamCustomer> {
    return this.http.get<CiamCustomer>(this.bffUrl(this.config.bff.customerPath));
  }

  logout(): Observable<void> {
    return this.http.delete<void>(this.bffUrl(this.config.bff.logoutPath), {
      withCredentials: true,
    }).pipe(
      catchError(error => {
        // La session locale doit toujours être fermée, même si la révocation distante échoue.
        console.error('Échec de la révocation de la session CIAM.', error);
        return of(undefined);
      }),
      finalize(() => this.storageService.signalLogout()),
    );
  }

  consumeReturnUrl(activated = false): string {
    const returnUrl = window.sessionStorage.getItem(RETURN_URL_KEY);
    window.sessionStorage.removeItem(RETURN_URL_KEY);
    return this.safeInternalUrl(returnUrl, activated ? '/creation' : DEFAULT_RETURN_URL);
  }

  async navigateAfterAuthentication(activated = false): Promise<boolean> {
    return this.router.navigateByUrl(this.consumeReturnUrl(activated));
  }

  private redirectToAuthApp(
    path: string,
    returnUrl: string,
    callbackParameter: string,
    callbackOverride?: string,
  ): void {
    this.assertEnabled();
    window.sessionStorage.setItem(RETURN_URL_KEY, this.safeInternalUrl(returnUrl));

    const authUrl = new URL(path, this.withTrailingSlash(this.config.authAppBaseUrl));
    const callbackUrl = callbackOverride ?? new URL(
      this.config.callbackPath,
      this.withTrailingSlash(environment.application.baseUrl),
    ).toString();

    authUrl.searchParams.set(callbackParameter, callbackUrl);
    window.location.assign(authUrl.toString());
  }

  /**
   * Le ré-échange du cookie ne rend que des jetons : l'identifiant client, que les pages
   * transmettent au backend, n'existe dans un nouvel onglet que si on le relit sur GCI.
   */
  private ensureCustomerIdentity(): Observable<void> {
    if (this.storageService.getUser()) {
      return of(undefined);
    }

    return this.getCurrentCustomer().pipe(
      map(customer => this.storeCustomerIdentity(customer)),
    );
  }

  private storeCustomerIdentity(customer: CiamCustomer): void {
    if (!customer.customerAtgId) {
      throw new Error('Le profil GCI ne contient pas customerAtgId.');
    }

    this.storageService.saveUser(customer.customerAtgId);
  }

  private safeInternalUrl(value?: string | null, fallback = DEFAULT_RETURN_URL): string {
    if (!value || !value.startsWith('/') || value.startsWith('//')) {
      return fallback;
    }

    return value;
  }

  private bffUrl(path: string): string {
    return `${this.config.bff.baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  private withTrailingSlash(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
  }

  private assertEnabled(): void {
    if (!this.config?.enabled || !this.config.authAppBaseUrl || !this.config.bff?.baseUrl) {
      throw new Error('La configuration CIAM Customer est absente ou incomplète.');
    }
  }

  /**
   * Un 400/401/403 sur le ré-échange signifie que le cookie AuthApp n'est plus exploitable :
   * la session est perdue pour de bon, là où une panne réseau ou une 5xx restent transitoires.
   * Exposé publiquement car l'intercepteur HTTP doit trancher de la même façon avant de
   * renvoyer l'utilisateur vers la connexion.
   */
  isDefinitiveRefreshRejection(error: unknown): boolean {
    return error instanceof HttpErrorResponse && [400, 401, 403].includes(error.status);
  }
}
